import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import { Add, CreateOutlined } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomPagination from '../../uiParts/CustomPagination';
import CustomSelectField from '../../uiParts/CustomSelectField';
import MaterialCard, { ItemProps } from '../../uiParts/MaterialCard';
import SideBar from '../../uiParts/SideBar';

import { response } from '../../../api/db';
import { deleteProduct, getMyProductList } from '../../../api/db/product';
import {
  gray90,
  gray100,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primaryColor
} from '../../../constants/Colors';
import { userType as userTypeEnum } from '../../../constants/Enum';
import { sideBarWidth } from '../../../constants/Location';
import { productMethodList } from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';
import { common } from '../../../modules/error';
import { MyProductGetProductType } from '../../../validation/products/productType';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  addButtonPc: {
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      display: 'none'
    }
  },
  addButtonSp: {
    display: 'none',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      display: 'flex'
    }
  },
  content: {
    pt: '96px',
    '@media screen and (max-width: 960px)': {
      p: '64px 16px 0'
    }
  },
  contentHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    p: '32px 32px 24px',
    '@media screen and (max-width: 960px)': {
      p: '24px 16px'
    },
    '@media screen and (max-width: 600px)': {
      flexDirection: 'column'
    },

    // for select-field
    '& .MuiSelect-select': {
      p: '17px 12px 10px 16px'
    },
    '& .MuiTypography-root.MuiTypography-body1': {
      m: '0'
    },
    '& .MuiTypography-root.MuiTypography-body1 fieldset': {
      height: '48px',
      top: '-5.5px'
    },
    '& .MuiTypography-root.MuiTypography-body1 p': {
      color: primaryColor,
      fontWeight: '500'
    },
    '& .MuiTypography-root.MuiTypography-body1 svg': {
      top: 'calc(50% - 10.5px)'
    }
  },
  contentTitle: {
    color: gray100,
    fontSize: '24px',
    fontWeight: '500',
    mt: '16px',
    textAlign: 'center',
    '@media screen and (max-width: 960px)': {
      textAlign: 'center',
      maxWidth: '170px'
    }
  },
  contentWrapper: {
    ml: sideBarWidth,
    width: '100%',
    '@media screen and (max-width: 960px)': {
      ml: '0'
    }
  },
  contentText: {
    color: gray100,
    mt: '24px',
    textAlign: 'center',
    '@media screen and (max-width: 960px)': {
      mt: '16px'
    }
  },
  deleteButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      display: 'none'
    }
  },
  deleteIcon: {
    color: gray90,
    display: 'none',
    fontSize: '32px',
    '&:hover': {
      opacity: '0.8'
    },
    '@media screen and (max-width: 600px)': {
      display: 'block'
    }
  },
  divider: {
    m: '0 32px',
    '@media screen and (max-width: 960px)': {
      m: '0 16px'
    }
  },
  editButton: {
    boxSizing: 'border-box',
    height: '44px',
    p: '10px 22px 10px 16px',
    '@media screen and (max-width: 600px)': {
      flexGrow: '1'
    }
  },
  headerLeft: {
    width: 'auto',
    '@media screen and (max-width: 600px)': {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  },
  headerRight: {
    columnGap: '16px',
    flexDirection: 'row',
    width: 'auto',
    '@media screen and (max-width: 600px)': {
      mt: '20px',
      width: '100%',
      '&.MuiGrid-container div': {
        width: '100%'
      }
    }
  },
  headerTitle: {
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px'
    }
  },
  icon: {
    fontSize: '80px',
    width: '100%',
    '@media screen and (max-width: 960px)': {
      fontSize: '64px'
    }
  },
  materialList: {
    p: '0 32px',
    '@media screen and (max-width: 960px)': {
      p: '0 16px'
    }
  },
  pageWrapper: {
    width: '100%',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column'
    }
  },
  registerButton: {
    mt: '80px',
    maxWidth: '343px'
  }
};

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const {
    isLoggedIn,
    setSnackbarOpen,
    setErrorText,
    userId,
    userRank,
    userType
  } = useContext(AuthContext);
  const currentUserType = Number(localStorage.getItem('user_type'));
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [displayList, setDisplayList] = useState<ItemProps[]>([]);
  // お気に入り数
  const [allCount, setAllCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  // 計算方法
  const [selectedMethod, setSelectedMethod] = useState(1);
  const handleChangeMethod = (event: SelectChangeEvent<unknown>) => {
    setSelectedMethod(event.target.value as number);

    window.dataLayer.push({
      event: 'co2_method',
      screen: window.location.pathname,
      category: 'pulldown',
      action: 'product_co2_method',
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });
  };
  // ページネーション
  const topRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    topRef?.current?.scrollIntoView();
  };

  // ログインチェック
  useEffect((): void => {
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    } else if (
      currentUserType !== userTypeEnum.maker &&
      currentUserType !== userTypeEnum.admin
    ) {
      navigate('/home');
      return;
    }
  }, [currentUserType]);

  // ページか計算方法を変更した時、登録した建材を取得
  useEffect((): void => {
    void (async () => {
      setLoading(true);
      try {
        const limit = 20;
        const offset = (currentPage - 1) * limit;
        const order = 1;

        // 登録した建材取得
        const fetchedData = await getMyProductList(
          limit,
          offset,
          order,
          selectedMethod
        );

        // 建材数を取得
        setAllCount(fetchedData.all_count);
        setTotalCount(fetchedData.total_count);

        // ページネーション
        const totalPages = Math.ceil(fetchedData.total_count / limit);
        setTotalPages(totalPages);

        // 表示用データに変換
        const displayList = fetchedData.products.map(
          (product: MyProductGetProductType) => {
            // 日付の表示形式を変更
            const updateDate = new Date(product.updated_at);
            const year = updateDate.getFullYear();
            const month = updateDate.getMonth() + 1;
            const day = updateDate.getDate();

            const formattedUpdatedDate = `${year}/${month}/${day}`;

            const sortedImageData = product.image.sort(
              (a, b) => ((a && a.sort) || 0) - ((b && b.sort) || 0)
            );

            return {
              productId: product.id,
              productInfoId: product.product_info_id,
              alias: product.alias,
              status: product.status,
              category: product.category.name,
              material: product.material.name,
              name: product.name,
              specification: product.specification,
              summary: product.summary,
              emissionIntensity: product.emission_intensity,
              reduceRate: product.reduce_rate,
              calculationValue: product.calculated_emission_intensity,
              calculationBasisType: [product.calculation_basis_type],
              calculationBasisName: product.calculation_basis_name,
              company: product.users.company,
              myListNum: product.mylist_number,
              score: product.evaluation_score,
              recommendFlg: [product.recommend_flg],
              title: product.evaluations.title,
              content: product.evaluations.content,
              updateDate: formattedUpdatedDate,
              myListFlg: Boolean(product.mylist_flg),
              favoriteFlg: Boolean(product.favorite_flg),
              referenceUnitPrice: product.reference_unit_price,
              reductionAmount: product.calculated_reduce_rate,
              price: product.reference_unit_price,
              images: sortedImageData,
              co2Unit: product.material.co2_unit,
              costUnit: product.material.cost_unit,
              calculatedEmissionIntensity:
                product.calculated_emission_intensity,
              calculatedMethod: product.calculated_method,
              calculatedReduceRate: product.calculated_reduce_rate
            };
          }
        );
        setDisplayList(displayList);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (!setSnackbarOpen || !setErrorText) return;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText(loginErrorResponse.data.message);
          setSnackbarOpen(true);
        } else {
          setErrorText(common);
          setSnackbarOpen(true);
        }
      } finally {
        setLoading(false);
        setShowList(true);
      }
    })();
  }, [currentPage, selectedMethod]);

  // 建材削除
  const deleteProductFun = async (productId: number) => {
    try {
      await deleteProduct(productId);
      window.dataLayer.push({
        event: 'delete',
        screen: window.location.pathname,
        category: 'button',
        action: 'product_delete',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (!setSnackbarOpen || !setErrorText) return;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setSnackbarOpen(true);
      } else {
        setErrorText(common);
        setSnackbarOpen(true);
      }
    } finally {
      navigate('/product/delete/complete');
    }
  };

  const content = () => {
    let content;
    if (allCount === 0) {
      // 建材未登録
      content = (
        <>
          <Divider sx={styles.divider} />
          <Grid
            container
            direction="column"
            alignItems="center"
            sx={styles.content}
          >
            <CreateOutlined sx={styles.icon} />
            <Typography sx={styles.contentTitle}>
              登録した建材はまだありません
            </Typography>
            <Typography sx={styles.contentText}>
              登録した建材がここに表示されます
            </Typography>
            <Button
              fullWidth
              onClick={() => navigate('/product/new')}
              sx={[styles.registerButton, primaryButtonOutlinedColor]}
              variant="outlined"
            >
              建材を登録する
            </Button>
          </Grid>
        </>
      );
    } else if (totalCount > 0) {
      // 表示建材あり
      content = (
        <Box sx={styles.materialList}>
          {displayList.map((item, index) => (
            <MaterialCard
              item={item}
              key={index}
              buttonSectionType="registered"
              mylistReviewSection={false}
              statusSection={true}
              deleteProductFun={deleteProductFun}
            />
          ))}
          {totalPages > 1 ? (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          ) : null}
        </Box>
      );
    } else {
      // 建材登録済かつ表示建材なし
      content = (
        <>
          <Divider sx={styles.divider} />
          <Grid
            container
            direction="column"
            alignItems="center"
            sx={styles.content}
          >
            <CreateOutlined sx={styles.icon} />
            <Typography sx={styles.contentTitle}>
              登録した建材はまだありません
            </Typography>
            <Typography sx={styles.contentText}>
              登録した建材がここに表示されます
            </Typography>
            <Button
              fullWidth
              onClick={() => navigate('/product/new')}
              sx={[styles.registerButton, primaryButtonOutlinedColor]}
              variant="outlined"
            >
              建材を登録する
            </Button>
          </Grid>
        </>
      );
    }

    return content;
  };

  return (
    <>
      <CustomHead title="建材登録一覧 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container ref={topRef} sx={styles.pageWrapper}>
        <SideBar />

        <Grid sx={styles.contentWrapper}>
          <Grid container sx={styles.contentHeader}>
            <Grid container sx={styles.headerLeft}>
              <Typography sx={styles.headerTitle}>建材登録</Typography>
              {totalCount > 0 && (
                <Button
                  onClick={() => navigate('/product/new')}
                  startIcon={<Add />}
                  sx={[primaryButtonBackgroundColor, styles.addButtonSp]}
                  variant="contained"
                >
                  新規登録
                </Button>
              )}
            </Grid>

            <Grid container sx={styles.headerRight}>
              <CustomSelectField
                list={productMethodList}
                onChange={handleChangeMethod}
                value={selectedMethod}
                type="primary"
              />
              {totalCount > 0 && (
                <Button
                  onClick={() => navigate('/product/new')}
                  startIcon={<Add />}
                  sx={[primaryButtonBackgroundColor, styles.addButtonPc]}
                  variant="contained"
                >
                  新規登録
                </Button>
              )}
            </Grid>
          </Grid>

          {showList && content()}
        </Grid>
      </Grid>
    </>
  );
};

export default ProductList;
